import { useRouter } from "@shared/client/navigation";
import { useState } from "react";
import { LoaderCircle } from "lucide-react";
import { signIn, signOut } from "@shared/lib/auth-client";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";

/**
 * Connexion par e-mail et mot de passe. Google n'est branché que sur l'app :
 * un compte Google se connecte depuis l'espace client, la session vaut ensuite
 * ici (cookie partagé, voir AUTH_COOKIE_DOMAIN).
 */
export function AdminLoginForm({ next, appUrl, deniedEmail }: { next: string; appUrl: string; deniedEmail: string | null }) {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await signIn(email, password);
      router.replace(next);
      router.refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Connexion impossible.");
      setBusy(false);
    }
  }

  async function switchAccount() {
    await signOut().catch(() => undefined);
    router.refresh();
  }

  return (
    <div className="w-full max-w-[380px]">
      <img alt="Hune" className="mb-6" height={30} src="/brand/Logo.svg" width={30} />
      <h1 className="mt-0 mb-2 font-serif text-[28px] leading-tight font-normal tracking-tight">Administration</h1>

      {deniedEmail ? (
        <div className="grid gap-4">
          <p className="m-0 text-[13.5px] leading-relaxed text-muted-foreground">
            Le compte <strong className="font-medium text-foreground">{deniedEmail}</strong> n’a pas accès à
            l’administration.
          </p>
          <Button onClick={switchAccount} variant="outline">Changer de compte</Button>
        </div>
      ) : (
        <form className="grid gap-4" onSubmit={submit}>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="grid gap-1.5">
            <Label htmlFor="email">E-mail</Label>
            <Input autoComplete="email" id="email" onChange={(e) => setEmail(e.target.value)} required type="email" value={email} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="password">Mot de passe</Label>
            <Input
              autoComplete="current-password"
              id="password"
              onChange={(e) => setPassword(e.target.value)}
              required
              type="password"
              value={password}
            />
          </div>
          <Button disabled={busy} type="submit">
            {busy && <LoaderCircle className="size-4 animate-spin" />}
            Se connecter
          </Button>
          <p className="m-0 text-[13px] text-muted-foreground">
            Compte Google ?{" "}
            <a className="text-primary underline-offset-2 hover:underline" href={`${appUrl}/auth/login`}>
              Connectez-vous depuis l’espace client
            </a>
            , puis revenez ici.
          </p>
        </form>
      )}
    </div>
  );
}
